// @flow

import { get, isNumber } from 'lodash';
import moment from 'moment';
import domainsService from '@clubmed/domains-service';

const EMAIL_PATTERN = /^(?!.*@.*\.{2})[^\s@]+@[^\s@]+\.[^\s@.]+$/;
const GM_NUMBER_PATTERN = /^[0-9]{0,9}$/;
const PHONE_NUMBER_PATTERN = /^ *\+? *(?:[ \-()]*[0-9][ \-()]*){6,12}$/;
const UNICODE_CHAR_REGEXP = /[^\u0000-\u00ff]/;

function _regexp(reg: RegExp): string => boolean {
  return value => reg.test(value);
}

function _minLength(length: number): string => boolean {
  return value => value.length >= length;
}

function _maxLength(length: number): string => boolean {
  return (value = '') => value.length <= length;
}

function _haveLength(length: number): string => boolean {
  return value => value.length === length;
}

function _enum(values: Array<*>): (*) => boolean {
  return (value: *) => values.indexOf(value) !== -1;
}

function _mandatory(): (*) => boolean {
  return (value: *) => !!value;
}

function _checkDate(format?: string): string => boolean {
  return dateToCheck => {
    return !!(dateToCheck && format && moment(dateToCheck, format, true).isValid());
  };
}

function _getAge(
  birthday: string,
  birthdayFormat: string,
  onDate?: string,
  onDateFormat?: string,
): number {
  const fromDate = onDate && onDateFormat ? moment(onDate, onDateFormat) : moment();
  return fromDate.diff(moment(birthday, birthdayFormat), 'years', true);
}

function _notContainsUnicode(str): boolean {
  return !UNICODE_CHAR_REGEXP.test(str);
}

function _inRange(min: number, max: number): number => boolean {
  return value => value >= min && value <= max;
}

/* Validators */
const email: FormFieldValidator = {
  name: 'email',
  isValid: _regexp(EMAIL_PATTERN),
};
const firstName: FormFieldValidator = {
  name: 'firstName',
  isValid: value => _maxLength(20)(value) && _notContainsUnicode(value),
};
const lastName: FormFieldValidator = {
  name: 'lastName',
  isValid: value => _maxLength(30)(value) && _notContainsUnicode(value),
};

const notContainsUnicode: FormFieldValidator = {
  name: 'notContainsUnicode',
  isValid: value => _notContainsUnicode(value),
};

const unicodeFirstName: FormFieldValidator = {
  name: 'unicodeFirstName',
  isValid: _maxLength(100),
};
const unicodeLastName: FormFieldValidator = {
  name: 'unicodeLastName',
  isValid: _maxLength(100),
};
const strictName: FormFieldValidator = {
  name: 'strictName',
  isValid: value =>
    _mandatory()(value) && _regexp(/^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/)(value),
};
const ASCII: FormFieldValidator = {
  name: 'ASCII',
  isValid: _regexp(/^[\x20-\xFF]+$/),
};

function minLength(length: number): FormFieldValidator {
  return {
    name: 'minLength',
    isValid: _minLength(length),
  };
}
function haveLength(length: number): FormFieldValidator {
  return {
    name: 'haveLength',
    isValid: _haveLength(length),
  };
}
function maxLength(length: number): FormFieldValidator {
  return {
    name: 'maxLength',
    isValid: _maxLength(length),
  };
}
function regexp(exp: RegExp): FormFieldValidator {
  return {
    name: 'regexp',
    isValid: _regexp(exp),
  };
}
function enum1(...values: Array<*>): FormFieldValidator {
  return {
    name: 'enum',
    isValid: _enum(values),
  };
}
function phoneNumber(locale?: ?string): FormFieldValidator {
  const phoneRegexp = new RegExp(
    get(domainsService.getDomain(locale), 'fields.phoneNumber', PHONE_NUMBER_PATTERN),
  );
  return {
    name: 'phoneNumber',
    isValid: _regexp(phoneRegexp),
  };
}

function mandatory(): FormFieldValidator {
  return {
    name: 'mandatory',
    isValid: _mandatory(),
  };
}
function date(format?: string): FormFieldValidator {
  return {
    name: 'date',
    isValid: _checkDate(format),
  };
}

type ageValidatorOptions = {
  ageRequired: number,
  birthdayFormat: string,
  onDate?: string,
  onDateFormat?: string,
};

const optionalBirthdate = ({
  ageRequired,
  birthdayFormat,
  onDate,
  onDateFormat,
}: ageValidatorOptions) => {
  return {
    name: 'optionalBirthdate',
    isValid: (birthdate: string) =>
      !birthdate ||
      (birthdayFormat &&
        moment(birthdate, birthdayFormat, true).isValid() &&
        isNumber(ageRequired) &&
        _checkDate(birthdayFormat)(birthdate) &&
        _getAge(birthdate, birthdayFormat, onDate, onDateFormat) >= ageRequired),
  };
};

function minAge({
  ageRequired,
  birthdayFormat,
  onDate,
  onDateFormat,
}: ageValidatorOptions): FormFieldValidator {
  return {
    name: 'minAge',
    isValid: birthday => {
      return (
        isNumber(ageRequired) &&
        _checkDate(birthdayFormat)(birthday) &&
        _getAge(birthday, birthdayFormat, onDate, onDateFormat) >= ageRequired
      );
    },
  };
}

function maxAge({
  ageRequired,
  birthdayFormat,
  onDate,
  onDateFormat,
}: ageValidatorOptions = {}): FormFieldValidator {
  return {
    name: 'maxAge',
    isValid: (birthday: number & string) => {
      return (
        isNumber(ageRequired) &&
        _checkDate(birthdayFormat)(birthday) &&
        _getAge(birthday, birthdayFormat, onDate, onDateFormat) < ageRequired
      );
    },
  };
}
function inRange(min: number, max: number): FormFieldValidator {
  return {
    name: 'inRange',
    isValid: _inRange(min, max),
  };
}
const gmNumber: FormFieldValidator = {
  name: 'gmNumber',
  isValid: (value: string) => _regexp(GM_NUMBER_PATTERN)(value),
};

function legalAgreement(required: boolean): FormFieldValidator {
  return {
    name: 'legalAgreement',
    // if legalAgreement is not required, return true to bypass validation
    isValid: value => (required ? value === true : true),
  };
}

export {
  EMAIL_PATTERN,
  GM_NUMBER_PATTERN,
  PHONE_NUMBER_PATTERN,
  email,
  firstName,
  lastName,
  unicodeFirstName,
  unicodeLastName,
  notContainsUnicode,
  strictName,
  ASCII,
  minLength,
  haveLength,
  maxLength,
  regexp,
  enum1 as enum,
  phoneNumber,
  mandatory,
  date,
  minAge,
  maxAge,
  gmNumber,
  inRange,
  legalAgreement,
  optionalBirthdate,
};
